// Configuration values
const config = {
  /**
   * REST and Socket.IO share the same HTTP server: use the **API base URL only** (no `/socket.io` in this string).
   * The socket.io client adds the engine path under the hood (see `socketPath`), e.g. `http://localhost:5001/socket.io/?EIO=4&...`.
   * Local: `http://localhost:<PORT>` — default 5001 matches backend when PORT is unset. Prod: `https://<api-host>`.
   */
  apiUrl: import.meta.env.VITE_API_URL || 'http://localhost:5001',
  /**
   * Engine path (default `/socket.io`). Override with `VITE_SOCKET_PATH` only if the server or reverse proxy rewrites it.
   */
  socketPath: (import.meta.env.VITE_SOCKET_PATH as string | undefined) || '/socket.io',
  
  // Environment detection
  isDevelopment: import.meta.env.DEV || false,
  isProduction: import.meta.env.PROD || false,
  
  // Feature flags
  enableDebugLogging: import.meta.env.VITE_ENABLE_DEBUG_LOGGING === 'true' || false,

  /**
   * Base URL for the student-facing app (used for "View" actions from Admin).
   * Example: https://join.americation.com
   * Local dev: http://localhost:3000 (or wherever the student app runs)
   */
  studentAppUrl: (import.meta.env.VITE_STUDENT_APP_URL as string | undefined) || '',
  
  // Session configuration - now dynamically loaded from backend
  // Fallback values only - real values come from /api/admin/auth/session-status
  fallbackSessionTimeout: 4 * 60 * 60 * 1000,    // 4 hours fallback
  fallbackSessionCheckInterval: 2 * 60 * 1000,   // 2 minutes fallback
  fallbackWarningThreshold: 3 * 60 * 1000,       // 3 minutes fallback
  fallbackTokenRefreshThreshold: 5 * 60 * 1000,  // 5 minutes fallback
  
  // API endpoints - corrected to match backend
  endpoints: {
    admin: {
      login: '/api/admin/auth/login',
      loginVerify2fa: '/api/admin/auth/login/verify-2fa',
      loginResendCode: '/api/admin/auth/login/resend-code',
      register: '/api/admin/auth/register',
      logout: '/api/admin/auth/logout',
      refresh: '/api/admin/auth/refresh',
      me: '/api/admin/auth/me',
      sessionStatus: '/api/admin/auth/session-status',
      verify: '/api/admin/auth/verify',
      permissions: '/api/admin/management/permissions',
    },
    auth: {
      logout: '/api/auth/logout',
      refresh: '/api/auth/refresh',
      me: '/api/me',
    }
  }
};

// Log the current configuration to help with debugging
if (config.enableDebugLogging) {
  console.log('Config loaded:', {
    apiUrl: config.apiUrl,
    environment: config.isDevelopment ? 'Development' : 'Production',
    debugLogging: config.enableDebugLogging,
    fallbackSessionTimeout: `${config.fallbackSessionTimeout / (60 * 60 * 1000)} hours`,
    fallbackSessionCheckInterval: `${config.fallbackSessionCheckInterval / (60 * 1000)} minutes`,
    fallbackWarningThreshold: `${config.fallbackWarningThreshold / (60 * 1000)} minutes`,
    note: 'Session configuration now loaded dynamically from backend',
    fallbackTokenRefreshThreshold: `${config.fallbackTokenRefreshThreshold / (60 * 1000)} minutes`
  });
}

// Function to validate configuration and 
export function validateConfig() {
  const issues: string[] = [];
  const warnings: string[] = [];
  
  // Check API URL
  if (!config.apiUrl) {
    issues.push('VITE_API_URL is not set');
  } else {
    try {
      const url = new URL(config.apiUrl);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') {
        issues.push(`Invalid API URL protocol: ${url.protocol}`);
      }
    } catch (e) {
      issues.push(`Invalid API URL format: ${config.apiUrl}`);
    }
  }
  
  // Check for common development issues
  if (config.isDevelopment) {
    if (config.apiUrl.includes('localhost') || config.apiUrl.includes('127.0.0.1')) {
      // Check if we're using the right port for local development
      const currentPort = window.location.port;
      const apiPort = new URL(config.apiUrl).port;
      
      if (currentPort && apiPort && currentPort !== apiPort) {
        warnings.push(`Frontend running on port ${currentPort}, API on port ${apiPort}. This may cause CORS issues.`);
      }
    }
  }
  
  // Check environment variables
  if (!import.meta.env.VITE_API_URL) {
    warnings.push('VITE_API_URL not set, using fallback URL');
  }
  
  return { issues, warnings, isValid: issues.length === 0 };
}

export default config; 